/**
 * CSV export utility
 */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  columns: Array<{ key: keyof T; header: string; format?: (value: T[keyof T]) => string }>,
  filename: string
): void {
  // dataがnull/undefinedの場合は空配列として扱う
  const safeData = Array.isArray(data) ? data : [];
  
  if (safeData.length === 0) {
    return;
  }

  // BOMを追加してExcelでの文字化けを防ぐ
  const BOM = '\uFEFF';
  
  // ヘッダー行
  const headers = columns.map((col) => escapeCSVValue(col.header)).join(',');
  
  // データ行
  const rows = safeData.map((item) =>
    columns
      .map((col) => {
        const value = item[col.key];
        if (col.format) {
          return escapeCSVValue(col.format(value));
        }
        if (value === null || value === undefined) {
          return '';
        }
        if (typeof value === 'object') {
          return escapeCSVValue(JSON.stringify(value));
        }
        return escapeCSVValue(String(value));
      })
      .join(',')
  );
  
  const csvContent = BOM + headers + '\n' + rows.join('\n');
  
  // ダウンロード
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * CSV値をエスケープ
 */
function escapeCSVValue(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * CSV import utility
 */
export function parseCSV<T>(
  csvContent: string,
  columnMapping: Record<string, keyof T>
): T[] {
  // BOMを削除
  const cleanContent = csvContent.replace(/^\uFEFF/, '');
  // 改行コードを統一
  const normalizedContent = cleanContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  // ダブルクォート内の改行を考慮してCSVを行に分割
  const lines = splitCSVLines(normalizedContent).filter((line) => line.trim());
  
  if (lines.length < 2) {
    throw new Error('CSVファイルにデータがありません');
  }
  
  const headers = parseCSVLine(lines[0]);
  const results: T[] = [];
  
  
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    // 列数が少なくてもパースを試みる（空の値として扱う）
    if (values.length === 0) continue;
    
    const item: Partial<T> = {};
    headers.forEach((header, index) => {
      const mappedKey = columnMapping[header.trim()];
      if (mappedKey) {
        const value = values[index] ?? '';
        (item as Record<string, unknown>)[mappedKey as string] = value.trim();
      }
    });
    
    results.push(item as T);
  }
  
  if (results.length > MAX_ROWS) {
    throw new Error(`データ行数が上限（${MAX_ROWS.toLocaleString()}行）を超えています（${results.length.toLocaleString()}行）`);
  }

  return results;
}

/**
 * ダブルクォート内の改行を考慮してCSVを行に分割
 */
function splitCSVLines(content: string): string[] {
  const lines: string[] = [];
  let currentLine = '';
  let inQuotes = false;
  
  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    
    if (char === '"') {
      // ダブルクォートの開始/終了をトグル
      // ただし、エスケープされたクォート("")の場合は除く
      if (content[i + 1] === '"') {
        currentLine += '""';
        i++;
      } else {
        inQuotes = !inQuotes;
        currentLine += char;
      }
    } else if (char === '\n' && !inQuotes) {
      // クォート外の改行は行の区切り
      lines.push(currentLine);
      currentLine = '';
    } else {
      currentLine += char;
    }
  }
  
  // 最後の行を追加
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines;
}

/**
 * CSVの1行をパース（カンマ区切り、クォート対応）
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
  }
  
  result.push(current);
  return result;
}

/**
 * ファイル選択してCSVを読み込む
 */
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ROWS = 10000;

export function importCSVFile(): Promise<string> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        reject(new Error('ファイルが選択されませんでした'));
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        reject(new Error(`ファイルサイズが上限（10MB）を超えています（${(file.size / 1024 / 1024).toFixed(1)}MB）`));
        return;
      }
      
      try {
        const text = await file.text();
        resolve(text);
      } catch (err) {
        reject(err);
      }
    };
    
    input.click();
  });
}
